/**
 * QuickSettingsPanel Component
 *
 * A slide-in settings panel for quick access to Serene Zone preferences
 * during exercises. Allows users to adjust audio, background type, and
 * volume without interrupting their session flow.
 *
 * Features:
 * - Slide-in animation from right side
 * - Click-outside and Escape key to close
 * - Focus trap while open for accessibility
 * - Live preview of changes (applied immediately)
 * - Serene-themed styling to match the calming aesthetic
 *
 * Accessibility:
 * - role="dialog" with aria-modal="true" and aria-labelledby
 * - Focus trap keeps keyboard navigation within panel
 * - Focus moves to close button when opened, restored to previous element when closed
 * - Escape key closes the panel
 * - Backdrop click closes the panel (aria-hidden)
 * - CSS transitions respect prefers-reduced-motion via Tailwind
 * - Screen reader announcement when panel opens
 * - All form controls have proper labels and descriptions
 * - focus-visible styles on all interactive elements
 *
 * Screen Reader Behavior:
 * - Announces "Quick settings panel opened" when panel appears
 * - Panel is labeled "Quick Settings" via aria-labelledby
 * - Audio toggle has description: "Play calming ambient sounds during exercises"
 * - Volume slider announces current value
 * - Radio group for background style is properly labeled
 * - Done button closes the panel
 */

'use client';

import { useEffect, useRef, useCallback, type KeyboardEvent } from 'react';
import { X, Volume2, VolumeX, Sparkles, Waves, Palette } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type {
  SerenePreferences,
  SereneBackgroundType,
} from '@/providers/serene-provider';

// ============================================================================
// Types
// ============================================================================

export interface QuickSettingsPanelProps {
  /** Whether the panel is visible */
  isOpen: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Current preferences */
  preferences: SerenePreferences;
  /** Callback when a preference changes */
  onPreferenceChange: <K extends keyof SerenePreferences>(
    key: K,
    value: SerenePreferences[K]
  ) => void;
  /** Audio volume level (0-100), managed externally */
  volume?: number;
  /** Callback when volume changes */
  onVolumeChange?: (volume: number) => void;
}

// ============================================================================
// Constants
// ============================================================================

const PANEL_WIDTH = 280;
const PANEL_ID = 'quick-settings-panel';
const TITLE_ID = 'quick-settings-title';

/**
 * Background type options with labels and icons
 */
const BACKGROUND_OPTIONS: Array<{
  value: SereneBackgroundType;
  label: string;
  description: string;
  icon: typeof Palette;
}> = [
  {
    value: 'gradient',
    label: 'Gradient',
    description: 'Gentle color blend',
    icon: Palette,
  },
  {
    value: 'particles',
    label: 'Particles',
    description: 'Floating particles',
    icon: Sparkles,
  },
  {
    value: 'wave',
    label: 'Waves',
    description: 'Rippling waves',
    icon: Waves,
  },
];

// ============================================================================
// Component
// ============================================================================

/**
 * QuickSettingsPanel - Quick-access settings for Serene Zone
 *
 * Provides controls for audio, background type, and volume in a
 * slide-in panel that can be accessed during exercises.
 */
export function QuickSettingsPanel({
  isOpen,
  onClose,
  preferences,
  onPreferenceChange,
  volume = 50,
  onVolumeChange,
}: QuickSettingsPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Store the previously focused element when opening
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  // Focus management: focus first interactive element on open
  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      // Small delay to ensure animation has started
      const timer = setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Restore focus when closing
  useEffect(() => {
    if (!isOpen && previousActiveElement.current) {
      previousActiveElement.current.focus();
    }
  }, [isOpen]);

  // Handle Escape key to close panel
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    },
    [onClose]
  );

  // Focus trap: keep focus within panel
  const handleFocusTrap = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusableElements = panelRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Shift+Tab on first element -> go to last
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
      // Tab on last element -> go to first
      else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    },
    []
  );

  // Combined keyboard handler
  const handleKeyDownCombined = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      handleKeyDown(event);
      handleFocusTrap(event);
    },
    [handleKeyDown, handleFocusTrap]
  );

  // Handle backdrop click to close
  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle audio toggle
  const handleAudioToggle = useCallback(
    (checked: boolean) => {
      onPreferenceChange('audioEnabled', checked);
    },
    [onPreferenceChange]
  );

  // Handle background type change
  const handleBackgroundChange = useCallback(
    (value: string) => {
      onPreferenceChange('backgroundType', value as SereneBackgroundType);
    },
    [onPreferenceChange]
  );

  // Handle volume change
  const handleVolumeChange = useCallback(
    (values: number[]) => {
      if (onVolumeChange && values[0] !== undefined) {
        onVolumeChange(values[0]);
      }
    },
    [onVolumeChange]
  );

  // Don't render anything if not open (for cleaner DOM)
  if (!isOpen) return null;

  return (
    <>
      {/* Screen reader announcement */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        Quick settings panel opened
      </div>

      {/* Backdrop - semi-transparent overlay */}
      <div
        className={cn(
          'fixed inset-0 z-40',
          'bg-[hsl(var(--serene-overlay))]',
          'transition-opacity duration-300 motion-reduce:duration-0',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        id={PANEL_ID}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className={cn(
          'fixed right-0 top-0 z-50',
          'h-full overflow-y-auto',
          'bg-[hsl(var(--serene-bg-primary))]',
          'border-l border-[hsl(var(--serene-border))]',
          'shadow-xl',
          // Slide-in animation (respects reduced motion preference)
          'transition-transform duration-300 ease-out motion-reduce:duration-0',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        style={{ width: PANEL_WIDTH }}
        onKeyDown={handleKeyDownCombined}
      >
        {/* Header */}
        <div
          className={cn(
            'flex items-center justify-between',
            'p-4',
            'border-b border-[hsl(var(--serene-border))]'
          )}
        >
          <h2
            id={TITLE_ID}
            className={cn(
              'text-lg font-medium',
              'text-[hsl(var(--serene-text-primary))]'
            )}
          >
            Quick Settings
          </h2>
          <Button
            ref={closeButtonRef}
            variant="ghost"
            size="icon"
            onClick={onClose}
            className={cn(
              'size-8',
              'text-[hsl(var(--serene-text-secondary))]',
              'hover:text-[hsl(var(--serene-text-primary))]',
              'hover:bg-[hsl(var(--serene-bg-tertiary))]',
              'focus-visible:ring-[hsl(var(--serene-focus-ring))]'
            )}
            aria-label="Close settings panel"
          >
            <X className="size-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="space-y-6 p-4">
          {/* Audio Toggle Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label
                htmlFor="audio-toggle"
                className={cn(
                  'flex items-center gap-2',
                  'text-[hsl(var(--serene-text-primary))]',
                  'cursor-pointer'
                )}
              >
                {preferences.audioEnabled ? (
                  <Volume2 className="size-4 text-[hsl(var(--serene-accent-primary))]" />
                ) : (
                  <VolumeX className="size-4 text-[hsl(var(--serene-text-muted))]" />
                )}
                Ambient Audio
              </Label>
              <Switch
                id="audio-toggle"
                checked={preferences.audioEnabled}
                onCheckedChange={handleAudioToggle}
                aria-describedby="audio-description"
                className={cn(
                  'data-[state=checked]:bg-[hsl(var(--serene-accent-primary))]',
                  'data-[state=unchecked]:bg-[hsl(var(--serene-bg-tertiary))]'
                )}
              />
            </div>
            <p
              id="audio-description"
              className="text-xs text-[hsl(var(--serene-text-muted))]"
            >
              Play calming ambient sounds during exercises
            </p>
          </div>

          {/* Volume Slider - Only show if audio is enabled */}
          {preferences.audioEnabled && onVolumeChange && (
            <div className="space-y-3">
              <Label
                htmlFor="volume-slider"
                className="text-[hsl(var(--serene-text-primary))]"
              >
                Volume: {volume}%
              </Label>
              <Slider
                id="volume-slider"
                variant="serene"
                value={[volume]}
                min={0}
                max={100}
                step={5}
                onValueChange={handleVolumeChange}
                aria-label={`Volume: ${volume}%`}
                className="w-full"
              />
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-[hsl(var(--serene-border))]" />

          {/* Background Type Section */}
          <div className="space-y-3">
            <Label className="text-[hsl(var(--serene-text-primary))]">
              Background Style
            </Label>
            <RadioGroup
              value={preferences.backgroundType}
              onValueChange={handleBackgroundChange}
              className="space-y-2"
              aria-label="Choose background style"
            >
              {BACKGROUND_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = preferences.backgroundType === option.value;

                return (
                  <label
                    key={option.value}
                    htmlFor={`bg-${option.value}`}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-lg p-3',
                      'border transition-colors duration-200',
                      isSelected
                        ? 'border-[hsl(var(--serene-accent-primary))] bg-[hsl(var(--serene-accent-primary)_/_0.1)]'
                        : 'border-[hsl(var(--serene-border))] hover:border-[hsl(var(--serene-border-strong))] hover:bg-[hsl(var(--serene-bg-secondary))]'
                    )}
                  >
                    <RadioGroupItem
                      value={option.value}
                      id={`bg-${option.value}`}
                      className={cn(
                        'border-[hsl(var(--serene-border-strong))]',
                        'text-[hsl(var(--serene-accent-primary))]',
                        'focus-visible:ring-[hsl(var(--serene-focus-ring))]'
                      )}
                    />
                    <Icon
                      className={cn(
                        'size-5',
                        isSelected
                          ? 'text-[hsl(var(--serene-accent-primary))]'
                          : 'text-[hsl(var(--serene-text-muted))]'
                      )}
                    />
                    <div className="flex-1">
                      <div
                        className={cn(
                          'text-sm font-medium',
                          isSelected
                            ? 'text-[hsl(var(--serene-text-primary))]'
                            : 'text-[hsl(var(--serene-text-secondary))]'
                        )}
                      >
                        {option.label}
                      </div>
                      <div className="text-xs text-[hsl(var(--serene-text-muted))]">
                        {option.description}
                      </div>
                    </div>
                  </label>
                );
              })}
            </RadioGroup>
          </div>

          {/* Divider */}
          <div className="border-t border-[hsl(var(--serene-border))]" />

          {/* Done Button */}
          <Button
            onClick={onClose}
            className={cn(
              'w-full',
              'bg-[hsl(var(--serene-accent-primary))]',
              'text-[hsl(var(--serene-text-on-gradient))]',
              'hover:bg-[hsl(var(--serene-interactive-hover))]',
              'active:bg-[hsl(var(--serene-interactive-active))]',
              'focus-visible:ring-[hsl(var(--serene-focus-ring))]'
            )}
          >
            Done
          </Button>
        </div>
      </div>
    </>
  );
}

QuickSettingsPanel.displayName = 'QuickSettingsPanel';
